import { AnimatePresence, motion } from "framer-motion";
import { Lock, MousePointer2, CheckCircle2, Wifi } from "lucide-react";
import { cn } from "@/lib/cn";

// The tester's PC: wallpaper, taskbar, tray agent, and the browser the agent
// opens for itself. Purely presentational — every value comes from `stage`,
// which the demo's timeline drives.

const TRAY_DOT = {
  idle: "bg-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.22)]",
  running: "bg-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.22)]",
  ok: "bg-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.22)]",
  bad: "bg-rose-400 shadow-[0_0_0_3px_rgba(251,113,133,0.22)]",
};

const TRAY_TEXT = {
  idle: "Connected · Idle",
  running: "Running a test",
  ok: "Connected · Idle",
  bad: "Connected · Idle",
};

function Field({ label, value, active, mono = true }) {
  return (
    <div className="relative">
      <span className="absolute -top-4 left-0.5 text-[0.5625rem] font-bold uppercase tracking-wider text-slate-400">
        {label}
      </span>
      <div
        className={cn(
          "flex min-h-[2rem] items-center rounded-lg border px-2.5 py-1.5 text-[0.6875rem] transition-all",
          mono && "font-mono",
          active
            ? "border-brand-500 bg-white ring-4 ring-brand-500/15 dark:bg-white/10"
            : "border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/5"
        )}
      >
        <span className="truncate text-slate-700 dark:text-slate-200">{value}</span>
        {active && (
          <span className="ml-0.5 inline-block h-3 w-px animate-pulse bg-brand-500" />
        )}
      </div>
    </div>
  );
}

export default function DesktopStage({ stage, reduced }) {
  const { browserOpen, url, cursor, active, values, paying, hang, receipt, shutter, toast, tray } =
    stage;

  return (
    <div className="relative flex h-[26rem] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-brand-800 via-brand-900 to-ink-950 shadow-soft dark:border-white/10 sm:h-[32rem] lg:h-[35rem]">
      {/* wallpaper */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_15%_0%,rgba(129,140,248,0.35),transparent_55%),radial-gradient(90%_80%_at_90%_15%,rgba(192,132,252,0.25),transparent_60%)]" />
      <div className="pointer-events-none absolute inset-0 bg-grid-dark bg-[length:32px_32px] opacity-60" />

      <div className="relative z-10 flex items-center gap-2 px-4 pt-3 text-[0.625rem] font-semibold uppercase tracking-widest text-white/60">
        <Wifi size={12} /> Tester&apos;s PC · Windows
      </div>

      {/* the browser the agent opens for itself */}
      <div className="relative flex-1">
        <motion.div
          initial={false}
          animate={
            browserOpen
              ? { opacity: 1, scale: 1, y: 0 }
              : { opacity: 0, scale: 0.94, y: 14 }
          }
          transition={{ duration: reduced ? 0 : 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-x-4 inset-y-3 flex flex-col overflow-hidden rounded-xl border border-black/30 bg-white shadow-2xl dark:bg-ink-900 sm:inset-x-6 sm:inset-y-4"
        >
          {/* window chrome */}
          <div className="flex shrink-0 items-center gap-2.5 border-b border-slate-200 bg-slate-100 px-3 py-2 dark:border-white/10 dark:bg-white/5 sm:gap-3">
            <div className="hidden gap-1.5 sm:flex">
              <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 font-mono text-[0.625rem] text-slate-500 dark:border-white/10 dark:bg-ink-950 dark:text-slate-400">
              <Lock size={10} className="shrink-0" />
              <span className="truncate">{url}</span>
            </div>
            <span className="hidden shrink-0 rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-[0.5625rem] font-bold uppercase tracking-wide text-brand-700 dark:border-brand-500/25 dark:bg-brand-500/10 dark:text-brand-300 sm:block">
              Agent&apos;s own browser
            </span>
          </div>

          {/* the app under test */}
          <div className="relative flex-1 overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5 text-[0.6875rem] text-slate-500 dark:border-white/10 dark:text-slate-400">
              <span className="flex items-center gap-2 font-bold text-slate-800 dark:text-white">
                <span className="h-4 w-4 rounded bg-gradient-to-br from-sky-400 to-blue-600" />
                Acme Store
              </span>
              <span>Cart · 1 item</span>
            </div>

            <div className="grid max-w-[19rem] gap-5 p-4 sm:p-5">
              <p className="text-sm font-bold text-slate-900 dark:text-white">Checkout</p>
              <Field label="Card number" value={values.card} active={active === "card"} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Expiry" value={values.exp} active={active === "exp"} />
                <Field label="CVC" value={values.cvc} active={active === "cvc"} />
              </div>

              <div className="relative mt-1 flex items-center justify-center gap-2 overflow-hidden rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 px-4 py-2.5 text-xs font-bold text-white">
                {paying && (
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                )}
                Pay $49.00
                <AnimatePresence>
                  {stage.ripple > 0 && (
                    <motion.span
                      key={stage.ripple}
                      initial={{ scale: 0, opacity: 0.85 }}
                      animate={{ scale: 9, opacity: 0 }}
                      transition={{ duration: reduced ? 0 : 0.6, ease: "easeOut" }}
                      className="pointer-events-none absolute h-3 w-3 rounded-full bg-white"
                    />
                  )}
                </AnimatePresence>
              </div>

              <AnimatePresence>
                {receipt && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[0.6875rem] font-semibold text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-300"
                  >
                    <CheckCircle2 size={14} /> Receipt #A-2291 · Paid $49.00
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* the moment the test fails */}
            <AnimatePresence>
              {hang && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-x-4 bottom-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[0.6875rem] font-semibold text-rose-600 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-300"
                >
                  Still spinning after 10s — no receipt, no error.
                </motion.div>
              )}
            </AnimatePresence>

            {/* the agent's pointer */}
            {/* Positioned in pixels, not percent: the checkout block is
                top-anchored and the same height at every breakpoint, so px
                keeps the pointer on its target when the stage resizes. */}
            <motion.div
              animate={{ opacity: cursor.visible ? 1 : 0 }}
              style={{ left: cursor.x, top: cursor.y }}
              transition={{ duration: reduced ? 0 : 0.3 }}
              className={cn(
                "pointer-events-none absolute z-20 drop-shadow-md",
                !reduced && "transition-[left,top] duration-500 ease-out"
              )}
            >
              <MousePointer2
                size={18}
                className="fill-slate-900 text-white dark:fill-white dark:text-slate-900"
              />
            </motion.div>

            {/* screenshot shutter */}
            <AnimatePresence>
              {shutter > 0 && (
                <motion.div
                  key={shutter}
                  initial={{ opacity: 0.8 }}
                  animate={{ opacity: 0 }}
                  transition={{ duration: reduced ? 0 : 0.45 }}
                  className="pointer-events-none absolute inset-0 z-30 bg-white"
                />
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* Windows toast from the tray */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: reduced ? 0 : 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-14 right-3 z-30 max-w-[15rem] rounded-xl border border-slate-200 bg-white/95 px-3 py-2.5 shadow-xl backdrop-blur dark:border-white/10 dark:bg-ink-900/95 sm:right-4"
          >
            <p className="text-xs font-bold text-slate-900 dark:text-white">{toast.title}</p>
            <p className="text-[0.6875rem] text-slate-500 dark:text-slate-400">{toast.body}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* taskbar */}
      <div className="relative z-20 flex h-11 shrink-0 items-center justify-center gap-2.5 border-t border-white/10 bg-ink-950/60 px-3 backdrop-blur-xl">
        <span className="h-5 w-5 rounded bg-white/15" />
        <span className="h-5 w-5 rounded bg-white/15" />
        <span className="hidden h-5 w-5 rounded bg-white/15 sm:block" />
        <div className="absolute right-3 flex items-center gap-2 text-[0.625rem] font-semibold text-white/85 sm:right-4">
          <span className="relative grid h-5 w-5 place-items-center rounded-md bg-gradient-to-br from-brand-400 to-brand-600 text-[0.5rem] text-white">
            ◆
            {!reduced && (
              <span className="absolute -inset-1 animate-pulse-ring rounded-lg border border-brand-400" />
            )}
          </span>
          <span className={cn("h-1.5 w-1.5 rounded-full", TRAY_DOT[tray])} />
          <span className="hidden xs:inline">AI-QA Agent — </span>
          {TRAY_TEXT[tray]}
        </div>
      </div>
    </div>
  );
}
